import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../../../components/Config/api.service";
import { notify } from "../../../components/Utils/Notifications";
import { formatMoney } from "../../../components/Utils/FormatUtils";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import LeaveCalculationDetail from "./LeaveCalculationDetail";

const LeaveDeparture = ({ setActiveTab, employee, currentContract, leaveDepartureId, refreshRecap, handleDeleteLeaveDeparture }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [dateDepart, setDateDepart] = useState("");
  const [dateRetour, setDateRetour] = useState("");
  const [leaveData, setLeaveData] = useState(null);
  const isDraft = leaveData?.state === "draft" || !leaveData;
  const [amounts, setAmounts] = useState({});
  const [showDetail, setShowDetail] = useState(false);
  const manualFields = leaveData?.manual_fields || [];
  const detail = leaveData?.detail;
  const formatDays = (value) => Number(value || 0).toLocaleString(dateLocale, { maximumFractionDigits: 1 });
  // Montants affichés : indemnité si la société verse une IC, régularisation en « Absence payée avec indemnité »
  const amountFields = [
    leaveData && leaveData.company_gestion_type !== "paid_leave_no_ic" && { field: "indemnite_conge_amount", label: t('employeeProfile.leaveDeparture.leaveAllowance') },
    leaveData && leaveData.company_gestion_type === "paid_leave_with_ic" && { field: "regule_amount", label: t('employeeProfile.leaveDeparture.regularizationAmount') },
  ].filter(Boolean);
  // Montant saisi refusé : vide (ce n'est pas 0), non numérique, ou indemnité négative
  const amountError = (field) => {
    const value = amounts[field];
    if (value === "" || value === undefined || value === null || !Number.isFinite(Number(value))) {
      return t('employeeProfile.leaveDeparture.amountRequired');
    }
    if (field === "indemnite_conge_amount" && Number(value) < 0) {
      return t('employeeProfile.leaveDeparture.amountNegative');
    }
    return null;
  };
  const amountsEditable = isDraft && !!leaveData;
  const amountsInvalid = amountsEditable && amountFields.some(({ field }) => amountError(field));

  useEffect(() => {
    // Arrondi au centime : le serveur compare aussi au centime pour repérer une correction
    const rounded = (value) => (value === undefined || value === null ? "" : Math.round(value * 100) / 100);
    setAmounts({
      indemnite_conge_amount: rounded(leaveData?.indemnite_conge_amount),
      regule_amount: rounded(leaveData?.regule_amount),
    });
  }, [leaveData]);
  const getNextDay = (dateValue) => {
    if (!dateValue) return "";
    const d = new Date(dateValue + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };
  useEffect(() => {
    if (leaveDepartureId) {
      apiService.getElementById("/leave_departure", leaveDepartureId)
        .then(res => {
          const data = res.data.leave_departure;
          setLeaveData(data);
          setDateDepart(data.date_depart || "");
          setDateRetour(data.date_retour || "");
        })
        .catch(() => setLeaveData(null));
    } else {
      setLeaveData(null);
      setDateDepart("");
      setDateRetour("");
    }
  }, [leaveDepartureId]);

  // If dateDepart changes, ensure dateRetour remains valid (strictly after depart)
  useEffect(() => {
    if (!dateDepart || !dateRetour) return;
    const dDepart = new Date(dateDepart + 'T00:00:00');
    const dRetour = new Date(dateRetour + 'T00:00:00');
    if (Number.isNaN(dDepart.getTime()) || Number.isNaN(dRetour.getTime())) return;
    if (dRetour <= dDepart) {
      // reset invalid return date
      setDateRetour('');
      notify(t('employeeProfile.leaveDeparture.returnDateReset'), '', 3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateDepart]);

  const handleLeaveDeparture = (action = "validate") => async (e) => {
    e.preventDefault();
    if (!dateDepart || !dateRetour) return notify(t('employeeProfile.leaveDeparture.datesRequired'), "", 3);

    // Validation: dateRetour must be strictly after dateDepart
    const dDepart = new Date(dateDepart + 'T00:00:00');
    const dRetour = new Date(dateRetour + 'T00:00:00');
    if (Number.isNaN(dDepart.getTime()) || Number.isNaN(dRetour.getTime())) {
      return notify(t('employeeProfile.leaveDeparture.invalidDateFormat'), "", 3);
    }

    if (dRetour <= dDepart) {
      return notify(t('employeeProfile.leaveDeparture.returnAfterDeparture'), "", 3);
    }

    // Total days calculation (should never be negative)
    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.ceil((dRetour - dDepart) / msPerDay);
    if (totalDays <= 0) {
      return notify(t('employeeProfile.leaveDeparture.invalidPeriod'), "", 3);
    }

    const payload = {
      employee_id: employee.id,
      contract_id: currentContract.id,
      date_depart: dateDepart,
      date_retour: dateRetour,
      action,
      leave_id: leaveData?.id,
      // Montants affichés : le serveur ne retient comme corrigés que ceux qui ont changé
      indemnities: amountsEditable ? amountFields.map(({ field }) => ({ field, amount: Number(amounts[field]) })) : [],
    };

    try {
      const response = await apiService.saveElement("/leave_departure", payload);
      setLeaveData(response.data.leave_departure);
      const messages = {
        validate: 'validated',
        draft: 'backToDraft',
        compute: 'computed',
      };
      notify(t(`employeeProfile.leaveDeparture.${messages[action] || 'saved'}`), "", 2);
      setActiveTab && refreshRecap();
    } catch (err) {
      notify(err.response?.data?.error || t('employeeProfile.leaveDeparture.saveError'), "", 3);
    }
  };

  // Revient au montant calculé pour un montant corrigé à la main
  const resetAmount = async (field) => {
    try {
      const response = await apiService.saveElement("/leave_departure", {
        employee_id: employee.id,
        contract_id: currentContract.id,
        date_depart: dateDepart,
        date_retour: dateRetour,
        action: "reset_amount",
        field,
        leave_id: leaveData?.id,
      });
      setLeaveData(response.data.leave_departure);
    } catch (err) {
      notify(err.response?.data?.error || t('employeeProfile.leaveDeparture.saveError'), "", 3);
    }
  };

  return (
    <div className="bg-white min-h-screen p-6 md:p-10">
      <h2 className="text-3xl font-bold mb-8 text-green-600 flex justify-between items-center">
        {t('employeeProfile.leaveDeparture.title')}
        <span className={leaveData?.state === "validated" ? "badge text-[var(--color-primary)]" : "badge text-[var(--color-orange)]"}>
          {leaveData?.state === "validated" ? t('employeeProfile.leaveDeparture.validatedBadge') : t('employeeProfile.leaveDeparture.draftBadge')}
        </span>
      </h2>
      <div className="max-w-3xl mx-auto grid grid-cols-1 gap-8">
        <div>
          <label className="block mb-1 font-medium">{t('employeeProfile.leaveDeparture.departureDate')}</label>
          <input
            type="date"
            className="input input-bordered w-full"
            value={dateDepart}
            onChange={e => setDateDepart(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">{t('employeeProfile.leaveDeparture.returnDate')}</label>
          <input
            type="date"
            className="input input-bordered w-full"
            value={dateRetour}
            min={getNextDay(dateDepart) || undefined}
            onChange={e => setDateRetour(e.target.value)}
          />
        </div>
        {isDraft && (
          <div className="flex justify-end">
            <button
              type="button"
              className="btn btn-outline text-[--color-primary] hover:bg-[--color-primary] hover:text-white"
              disabled={amountsInvalid}
              onClick={handleLeaveDeparture("compute")}
            >
              {t('employeeProfile.leaveDeparture.calculate')}
            </button>
          </div>
        )}
        {amountFields.map(({ field, label }) => (
          <div key={field} className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50 rounded-lg p-4">
            <span className="font-bold text-xl flex items-center gap-2">
              {label}
              {manualFields.includes(field) && (
                <span className="badge text-[var(--color-orange)]">{t('employeeProfile.leaveDeparture.manualBadge')}</span>
              )}
            </span>
            {isDraft ? (
              <div className="flex items-center gap-3">
                {manualFields.includes(field) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline text-[--color-primary] hover:bg-[--color-primary] hover:text-white"
                    onClick={() => resetAmount(field)}
                  >
                    {t('employeeProfile.leaveDeparture.resetAmount')}
                  </button>
                )}
                <div className="flex flex-col">
                  <input
                    type="number"
                    required
                    min={field === "indemnite_conge_amount" ? 0 : undefined}
                    step="any"
                    aria-label={label}
                    aria-invalid={!!amountError(field)}
                    className={`input input-bordered ${amountError(field) ? "input-error" : ""}`}
                    value={amounts[field]}
                    onChange={e => setAmounts(prev => ({ ...prev, [field]: e.target.value }))}
                  />
                  {amountError(field) && (
                    <span className="text-sm text-red-600 mt-1">{amountError(field)}</span>
                  )}
                </div>
              </div>
            ) : (
              <span className="font-bold text-2xl text-[--color-primary]">{formatMoney(leaveData?.[field] || 0)}</span>
            )}
          </div>
        ))}
        {detail?.days_missing > 0 && (
          <div className="alert bg-orange-50 border border-[var(--color-orange)] text-gray-800">
            <span>
              {/* Solde nul ou négatif : « n'en a que 0 / -3 » ne se lit pas */}
              {t(`employeeProfile.leaveDeparture.${detail.days_available > 0 ? 'insufficientBalance' : 'insufficientBalanceNone'}`, {
                taken: formatDays(detail.days_taken),
                available: formatDays(detail.days_available),
                count: formatDays(detail.days_missing),
              })}
            </span>
          </div>
        )}
        {detail && (
          <div className="border rounded-lg">
            <button
              type="button"
              className="w-full flex justify-between items-center p-4 font-medium"
              onClick={() => setShowDetail(!showDetail)}
            >
              {showDetail ? t('employeeProfile.leaveDeparture.hideDetail') : t('employeeProfile.leaveDeparture.showDetail')}
              {showDetail ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
            </button>
            {showDetail && (
              <div className="px-4 pb-4">
                <LeaveCalculationDetail detail={detail} gestionType={leaveData.company_gestion_type} />
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-4 mt-8">
          {!isDraft ? (
            <>
              <button
                type="button"
                className="btn btn-outline text-[--color-primary] hover:bg-[--color-primary]"
                onClick={handleLeaveDeparture("draft")}
              >
                {t('employeeProfile.leaveDeparture.backToDraftButton')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("Contrats")}
                className="btn btn-outline btn-error"
              >
                {t('employeeProfile.leaveDeparture.back')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-outline text-[--color-primary] hover:bg-[--color-primary]"
                disabled={amountsInvalid}
                onClick={handleLeaveDeparture("save")}
              >
                {t('employeeProfile.leaveDeparture.save')}
              </button>
              <button
                onClick={handleLeaveDeparture("validate")}
                disabled={amountsInvalid}
                type="submit"
                className="btn bg-[--color-primary] text-white hover:bg-[--bg-hover]"
              >
                {t('employeeProfile.leaveDeparture.validate')}
              </button>
              <button
                type="button"
                onClick={leaveDepartureId ? handleDeleteLeaveDeparture : () => setActiveTab("Contrats")}
                className="btn btn-outline btn-error"
              >
                {t('employeeProfile.leaveDeparture.delete')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveDeparture;